import { Popper, styled } from '@mui/material';

import { dropdownClassNames } from './classNames';
import { AutocompleteSize } from './types';
import { tokens } from '../../../theme/generated/tokens';
import {
  DsFormHelperText,
  DsFormHelperTextProps,
} from '../form-helper-text/DsFormHelperText';
import DsFormLabel, { FormLabelProps } from '../form-label/FormLabel';
// DotsLoader, FormLabel, IconButton, Input, LabelChip, LabeledRadio, MultipleProgressBarsContainer, PreviewConfigContainer, ProgressBar, SkeletonTable, Switch, TextArea, ToggleButtonGroup, Legacy dropdowns
export const autocompleteSizeToFormLabelSize = (
  size?: AutocompleteSize
): FormLabelProps['size'] => {
  switch (size) {
    case 'small':
      return 'sm';
    case 'large':
      return 'lg';
    default:
      return 'md';
  }
};

export const MainContainer = styled('div', {
  target: dropdownClassNames.container,
})({
  display: 'grid',
  gridTemplateColumns: 'auto 1fr auto',
  columnGap: '8px',
});

export const Label = styled(DsFormLabel, {
  target: dropdownClassNames.label,
})<
  {
    hasOutsidePrefix: boolean;
    hasOutsideSuffix: boolean;
  } & FormLabelProps
>(({ hasOutsidePrefix, hasOutsideSuffix }) => ({
  gridColumnStart: hasOutsidePrefix ? 2 : 1,
  gridColumnEnd: hasOutsideSuffix ? 3 : 4,
}));

export const ToggleButton = styled('button', {
  target: dropdownClassNames.toggle,
})<{
  isOpen: boolean;
  error: boolean;
  success: boolean;
  size: AutocompleteSize;
  disabled: boolean;
  readOnly: boolean;
  hasOutsidePrefix: boolean;
  hasOutsideSuffix: boolean;
}>(
  ({
    isOpen,
    error,
    success,
    size,
    disabled,
    readOnly,
    theme,
    hasOutsidePrefix,
    hasOutsideSuffix,
  }) => {
    // `disabled` is also true when readOnly (callers pass disabled || readOnly).
    // Prefer disabled tokens (same mapping as Button secondary disabled) when not readOnly.
    const isDisabledOnly = disabled && !readOnly;

    const getBorderColor = () => {
      if (isDisabledOnly) return tokens.theme.disabled.border;
      if (success) return tokens.theme.border.status.success;
      if (isOpen) return tokens.theme.border.action.neutralHover;
      if (error) return tokens.theme.border.status.error;
      return tokens.theme.border.structural.neutral;
    };

    const getHoverBorderColor = () => {
      if (disabled) return tokens.theme.disabled.border;
      if (success) return tokens.theme.border.status.success;
      if (readOnly) return tokens.theme.border.structural.neutral;
      return tokens.theme.border.action.neutralHover;
    };

    const getColor = () => {
      if (isDisabledOnly) return tokens.theme.disabled.fg;
      if (readOnly) return tokens.theme.readonly.fg;
      if (disabled) return tokens.theme.disabled.fg;
      return tokens.theme.fg.structural.primary;
    };

    const getBackgroundColor = () => {
      if (isDisabledOnly) return tokens.theme.disabled.bg;
      if (readOnly) return tokens.theme.readonly.bg;
      return tokens.theme.bg.structural.surface;
    };

    return {
      all: 'unset',
      transition: 'border-color, color, background-color 0.2s',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: getBorderColor(),
      borderRadius: tokens.theme.radius.sm,
      ...theme.typography[size === 'small' ? 'P12R' : 'P14R'],
      color: getColor(),
      display: 'flex',
      alignItems: 'center',
      cursor: disabled ? 'not-allowed' : readOnly ? 'default' : 'pointer',
      backgroundColor: getBackgroundColor(),

      gridColumnStart: hasOutsidePrefix ? 2 : 1,
      gridColumnEnd: hasOutsideSuffix ? 3 : 4,

      '&:hover': {
        borderColor: getHoverBorderColor(),
      },

      '&:focus': {
        borderColor: isDisabledOnly
          ? tokens.theme.disabled.border
          : tokens.theme.border.selection.primary,
      },
    };
  }
);

export const SelectedItemsContainer = styled('span', {
  target: dropdownClassNames.selectedItems,
})<{
  size: AutocompleteSize;
}>(({ size }) => ({
  padding: size === 'large' ? '8px 10px' : '4px 10px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  width: '100%',
}));

export const CaretContainer = styled('span', {
  target: dropdownClassNames.caret,
})<{
  isOpen: boolean;
  size: AutocompleteSize;
  disabled?: boolean;
}>(({ isOpen, size, disabled }) => ({
  marginLeft: 'auto',
  display: 'flex',
  alignItems: 'center',
  padding: size === 'large' ? '8px 10px' : '4px 10px',
  transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
  transition: 'transform 0.2s',
  svg: {
    color: disabled
      ? tokens.theme.disabled.fg
      : tokens.theme.fg.structural.secondary,
  },
}));

export const HintMessage = styled(DsFormHelperText, {
  target: dropdownClassNames.hint,
})<
  {
    hasOutsidePrefix: boolean;
    hasOutsideSuffix: boolean;
  } & DsFormHelperTextProps & { size: AutocompleteSize }
>(({ hasOutsidePrefix, hasOutsideSuffix, size }) => ({
  gridColumnStart: hasOutsidePrefix ? 2 : 1,
  gridColumnEnd: hasOutsideSuffix ? 3 : 4,
  ...(() => {
    if (size === 'small') {
      return {
        fontSize: '10px',
        lineHeight: '16px',
      };
    }
    return {
      fontSize: '12px',
      lineHeight: '18px',
    };
  })(),
}));

export const PrefixContainer = styled('span', {
  target: dropdownClassNames.prefix,
})<{
  size: AutocompleteSize;
}>(({ size }) => ({
  borderRight: '1px solid',
  borderColor: tokens.theme.border.structural.neutral,
  padding: size === 'large' ? '8px 10px' : '4px 10px',
  display: 'flex',
  alignItems: 'center',
}));

export const PlaceholderContainer = styled('span', {
  target: dropdownClassNames.placeholder,
})<{
  size: AutocompleteSize;
  disabled?: boolean;
}>(({ size, disabled }) => ({
  color: disabled
    ? tokens.theme.disabled.fg
    : tokens.theme.fg.structural.secondary,
  padding: size === 'large' ? '8px 10px' : '4px 10px',
}));

export const SearchInputContainer = styled('div', {
  target: dropdownClassNames.searchInputContainer,
})(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  borderBottom: '1px solid',
  borderColor: tokens.theme.border.structural.neutral,
  padding: '8px 12px',
  color: tokens.theme.fg.structural.secondary,
}));

export const SearchInput = styled('input', {
  target: dropdownClassNames.searchInput,
})(({ theme }) => ({
  all: 'unset',
  flex: 1,
  ...theme.typography.P14R,
  color: tokens.theme.fg.structural.primary,
  '&::placeholder': {
    color: tokens.theme.fg.structural.secondary,
  },
}));

export const MenuList = styled('div', {
  target: dropdownClassNames.menuList,
})(() => ({
  all: 'unset',
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
}));

export const CustomItemHint = styled('div', {
  target: dropdownClassNames.customItemHint,
})(({ theme }) => ({
  padding: '8px 12px',
  color: tokens.theme.fg.structural.primary,
  ...theme.typography.P12R,
}));

export const DropdownContainer = styled('div', {
  target: dropdownClassNames.dropdownContainer,
})(() => ({
  background: tokens.theme.bg.structural.surface,
  boxShadow: '0px 2px 8px 2px rgba(93, 110, 117, 0.25)',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: tokens.theme.radius.sm,
  overflow: 'hidden',
}));

export const MenuItem = styled('div', {
  target: dropdownClassNames.menuItem,
})<{
  isDisabled: boolean;
  isHighlighted: boolean;
  isSelected: boolean;
  isIndented?: boolean;
}>(({ isDisabled, isHighlighted, isSelected, isIndented }) => {
  const getItemTextColor = () => {
    if (isHighlighted && isSelected)
      return tokens.theme.fg.selection.primaryHover;
    if (isHighlighted) return tokens.theme.fg.structural.primary;
    if (isSelected) return tokens.theme.fg.selection.primary;
    return tokens.theme.fg.structural.primary;
  };

  const getItemBackgroundColor = () => {
    if (isHighlighted && isSelected)
      return tokens.theme.bg.selection.primarySubtleHover;
    if (isHighlighted) return tokens.theme.bg.action.surfaceHover;
    if (isSelected) return tokens.theme.bg.selection.primarySubtle;
    return 'transparent';
  };

  return {
    all: 'unset',
    transition: 'background-color, color 0.2s',
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    padding: '8px 12px',
    color: getItemTextColor(),
    backgroundColor: getItemBackgroundColor(),
    display: 'flex',
    overflowWrap: 'break-word',
    paddingLeft: isIndented ? '24px' : '12px',
  };
});

export const GroupHeaderItem = styled('div', {
  target: dropdownClassNames.groupHeaderItem,
})<{
  isDisabled?: boolean;
  isHighlighted?: boolean;
  isSelected?: boolean;
}>(({ isDisabled, isHighlighted, isSelected, theme }) => {
  const getItemTextColor = () => {
    if (isHighlighted && isSelected)
      return tokens.theme.fg.selection.primaryHover;
    if (isHighlighted) return tokens.theme.fg.structural.primary;
    if (isSelected) return tokens.theme.fg.selection.primary;
    return tokens.theme.fg.structural.secondary;
  };

  const getItemBackgroundColor = () => {
    if (isHighlighted && isSelected)
      return tokens.theme.bg.selection.primarySubtleHover;
    if (isHighlighted) return tokens.theme.bg.action.surfaceHover;
    if (isSelected) return tokens.theme.bg.selection.primarySubtle;
    return 'transparent';
  };

  return {
    all: 'unset',
    ...theme.typography.L10B,
    transition: 'background-color, color 0.2s',
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    opacity: isDisabled ? 0.4 : 1,
    padding: '8px 12px',
    color: getItemTextColor(),
    backgroundColor: getItemBackgroundColor(),
    display: 'block',
    overflowWrap: 'break-word',
  };
});

export const NoResultsMessage = styled('div', {
  target: dropdownClassNames.noResultsMessage,
})(({ theme }) => ({
  padding: '8px 12px',
  color: tokens.theme.fg.structural.secondary,
  ...theme.typography.P12R,
}));

export const StyledPopper = styled(Popper, {
  target: dropdownClassNames.popper,
})(({ theme }) => ({
  width: '100%',
  maxWidth: '400px',
  zIndex: theme.zIndex.drawer,
}));

export const PrefixOutside = styled('div', {
  target: dropdownClassNames.prefixOutside,
})<{ inputHasLabel: boolean }>`
  grid-column-start: 1;
  grid-row-start: ${(props) => (props.inputHasLabel ? 2 : 1)};
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const SuffixOutside = styled('div', {
  target: dropdownClassNames.suffixOutside,
})<{ inputHasLabel: boolean }>`
  grid-column-start: 3;
  grid-row-start: ${(props) => (props.inputHasLabel ? 2 : 1)};
  display: flex;
  align-items: center;
  justify-content: center;
`;
